/**
 * File: web/src/lib/jwt.ts
 * =======================
 *
 * JWT helpers (decode + expiry calculations)
 *
 * Purpose:
 * - Decode the JWT payload (claims) for client-side UX decisions.
 * - Determine if a token is expired and compute time until expiry.
 *
 * When used:
 * - useAuthGuard.ts schedules re-login before token expiration.
 *
 * Learner note:
 * - We do NOT verify JWT signatures here. Verification is enforced server-side by API Gateway.
 * - Client-side decoding is safe for UX decisions (hide/show admin UI), not security boundaries.
 *
 * Gotcha:
 * - exp is in seconds, while Date.now() is in milliseconds.
 */

export function decodeJwtPayload(token: string): any | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;

    // JWT payload is base64url encoded. Convert to base64 for atob().
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");

    // Add required padding for base64 decoding.
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);

    return JSON.parse(atob(padded));
  } catch {
    // If parsing fails, treat token as unusable.
    return null;
  }
}

/**
 * Expiry check with skew:
 * - skewSeconds makes us treat the token as "expired soon" slightly early.
 * - This reduces edge cases where a token expires during an API call/upload step.
 */
export function isTokenExpired(token: string, skewSeconds = 60): boolean {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;

  // No exp claim => treat as invalid/expired.
  if (typeof exp !== "number") return true;

  const now = Math.floor(Date.now() / 1000);
  return now >= exp - skewSeconds;
}

/**
 * Compute milliseconds until we should consider the token "too close to expiry".
 * Used for scheduling a redirect in useAuthGuard.
 */
export function msUntilExpiry(token: string, skewSeconds = 60): number {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;

  if (typeof exp !== "number") return 0;

  const now = Math.floor(Date.now() / 1000);
  return Math.max(0, (exp - skewSeconds - now) * 1000);
}
